import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { apiFetch } from '../utils/api-fetch';
import {
  Mail,
  Upload,
  Send,
  FileSpreadsheet,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Trash2,
  Save,
  Eye,
  Download,
  RefreshCw,
  Clock,
  CalendarClock,
} from 'lucide-react';
import { toast } from 'sonner';
import { ScheduledCampaignsPanel } from './ScheduledCampaignsPanel';
import { EmailCampaignHistoryPanel } from './EmailCampaignHistoryPanel';
import { useScheduledCampaigns } from '../hooks/useScheduledCampaigns';
import { useEmailCampaignHistory } from '../hooks/useEmailCampaignHistory';
import { useEmailSites } from '../hooks/useEmailSites';
import {
  buildDeliveryConfigFromForm,
  type EmailDeliveryConfig,
  type EmailContentFormat,
} from '../types/email-sites';
import { COMMON_TIMEZONES } from '../types/bulk-email-scheduling';
import type { BulkEmailSendConfig } from '../types/bulk-email-scheduling';
import { buildTemplatePreview, resolveSiteBranding } from '../utils/template-preview';
import { validateContentForSelectedSite, rewriteContentToSelectedSite } from '../utils/site-content-guard';
import { filterTemplatesForSite, templateBelongsToSite } from '../utils/template-site';
import { templateHasUtmTracking, utmTrackingRequirementMessage } from '../utils/template-utm';
import {
  SendAudienceReviewDialog,
  type AudienceCandidate,
} from './SendAudienceReviewDialog';

const RECIPIENT_TABLE_PAGE_SIZE = 100;

interface ParsedFileData {
  columns: string[];
  totalRows: number;
  preview: any[];
}

interface EmailTemplate {
  id?: string;
  name: string;
  subject: string;
  template: string;
  is_default?: boolean;
  delivery_config?: EmailDeliveryConfig | null;
  created_at?: string;
  updated_at?: string;
}

interface SendResult {
  email: string;
  name: string;
  success: boolean;
  messageId?: string;
  error?: string;
}

interface ManualRecipient {
  email: string;
  name: string;
}

async function parseJsonResponse(response: Response): Promise<Record<string, unknown>> {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    if (response.status === 404) {
      throw new Error(
        'Bulk email schedule API not found. Start the tools backend: cd tools/backend && npm run dev'
      );
    }
    throw new Error(
      `Server returned ${response.status} (expected JSON). Is the tools backend running on port 3010?`
    );
  }
  return response.json();
}

const BulkEmailManagement = () => {
  const [activeTab, setActiveTab] = useState('upload');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParsedFileData | null>(null);
  const [fileData, setFileData] = useState<any[]>([]);
  const [selectedRecipients, setSelectedRecipients] = useState<Set<number>>(new Set());
  const [selectedEmailColumn, setSelectedEmailColumn] = useState<string>('');
  const [selectedNameColumn, setSelectedNameColumn] = useState<string>('');
  const [customFields, setCustomFields] = useState<Record<string, string>>({});
  const [emailSubject, setEmailSubject] = useState('');
  const [emailTemplate, setEmailTemplate] = useState('');
  const [emailContentFormat, setEmailContentFormat] = useState<EmailContentFormat>('html');
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResults, setSendResults] = useState<SendResult[]>([]);
  const [templateName, setTemplateName] = useState('');
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const emailMessageRef = useRef<HTMLTextAreaElement>(null);

  // Manual email entry
  const [manualEmails, setManualEmails] = useState<string>('');
  const [manualRecipients, setManualRecipients] = useState<ManualRecipient[]>([]);
  const [manualInputMode, setManualInputMode] = useState<'simple' | 'columns'>('simple');
  const [manualColumnData, setManualColumnData] = useState<string>('');
  const [manualParsedColumns, setManualParsedColumns] = useState<string[]>([]);
  const [manualParsedData, setManualParsedData] = useState<any[]>([]);
  const [inputMode, setInputMode] = useState<'file' | 'manual' | 'both'>('file');

  const [sendMode, setSendMode] = useState<'now' | 'schedule'>('now');
  const [scheduledAt, setScheduledAt] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [scheduleTimezone, setScheduleTimezone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  );
  const [sendConfig, setSendConfig] = useState<BulkEmailSendConfig | null>(null);
  const scheduledCampaignsState = useScheduledCampaigns();
  const emailCampaignHistory = useEmailCampaignHistory();
  const emailSites = useEmailSites();
  const [recipientListPage, setRecipientListPage] = useState(0);
  const [showAudienceReview, setShowAudienceReview] = useState(false);
  const [audienceAction, setAudienceAction] = useState<'send' | 'schedule'>('send');
  const [audienceCandidates, setAudienceCandidates] = useState<AudienceCandidate[]>([]);
  const [sendOverride, setSendOverride] = useState<{
    manual: ManualRecipient[];
    fileIndices: Set<number>;
  } | null>(null);
  const activeScheduledCount = scheduledCampaignsState.campaigns.filter((c) =>
    ['pending', 'queued', 'processing', 'paused'].includes(c.status)
  ).length;

  useEffect(() => {
    fetchSendConfig();
  }, []);

  useEffect(() => {
    if (!emailSites.loading) {
      void fetchTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when website changes
  }, [emailSites.selectedSiteId, emailSites.loading]);

  useEffect(() => {
    if (!selectedTemplate) return;
    const stillVisible = templates.some(
      (template) =>
        template.id === selectedTemplate &&
        templateBelongsToSite(template, emailSites.selectedSiteId)
    );
    if (!stillVisible) {
      setSelectedTemplate('');
    }
  }, [emailSites.selectedSiteId, selectedTemplate, templates]);

  const fetchSendConfig = async () => {
    try {
      const response = await apiFetch('/bulk-email/settings');
      const data = await response.json();
      if (data.success && data.config) setSendConfig(data.config as BulkEmailSendConfig);
    } catch {
      /* settings optional until migration */
    }
  };

  const saveSendConfig = async (patch: Partial<BulkEmailSendConfig>) => {
    try {
      const response = await apiFetch('/bulk-email/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const data = await response.json();
      if (data.success) {
        setSendConfig(data.config as BulkEmailSendConfig);
        toast.success('Send settings saved');
      }
    } catch {
      toast.error('Could not save send settings');
    }
  };

  const getCurrentDeliveryConfig = () =>
    buildDeliveryConfigFromForm({
      siteId: emailSites.selectedSiteId,
      contentFormat: emailContentFormat,
    });

  const applyDeliveryConfig = (config?: EmailDeliveryConfig | null) => {
    setEmailContentFormat(config?.contentFormat ?? 'html');
    if (config?.siteId && emailSites.sites.some((site) => site.id === config.siteId)) {
      emailSites.selectSite(config.siteId);
    }
  };

  const templateMatchesSelectedSite = (template: EmailTemplate): boolean =>
    templateBelongsToSite(template, emailSites.selectedSiteId);

  const templatesForSelectedSite = filterTemplatesForSite(templates, emailSites.selectedSiteId);

  const appendSendMetadata = (formData: FormData) => {
    if (emailSites.selectedSiteId) {
      formData.append('siteId', emailSites.selectedSiteId);
    }
    if (selectedTemplate) {
      formData.append('templateId', selectedTemplate);
    }
    formData.append('contentFormat', emailContentFormat);
    const deliveryConfig = getCurrentDeliveryConfig();
    if (deliveryConfig) {
      formData.append('deliveryConfig', JSON.stringify(deliveryConfig));
    }
  };

  const sendMetadataJson = () => {
    const payload: Record<string, unknown> = {
      siteId: emailSites.selectedSiteId,
      contentFormat: emailContentFormat,
    };
    if (selectedTemplate) payload.templateId = selectedTemplate;
    const deliveryConfig = getCurrentDeliveryConfig();
    if (deliveryConfig) payload.deliveryConfig = deliveryConfig;
    return payload;
  };

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const query = emailSites.selectedSiteId
        ? `?siteId=${encodeURIComponent(emailSites.selectedSiteId)}`
        : '';
      const response = await apiFetch(`/bulk-email/templates${query}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setTemplates(data.templates || []);
        if (data.message && data.message.includes('table not found')) {
          console.warn(
            'Email templates table not found. Templates feature will be unavailable until migration is run.'
          );
          toast.warning('Email templates table not found. Please run the database migration.');
        } else if (data.templates && data.templates.length > 0) {
          console.log(`✅ Loaded ${data.templates.length} template(s)`);
          const defaultTemplate = (data.templates as EmailTemplate[]).find((t) => t.is_default);
          if (defaultTemplate && templateMatchesSelectedSite(defaultTemplate)) {
            setEmailSubject((prev) => prev || defaultTemplate.subject);
            setEmailTemplate((prev) => prev || defaultTemplate.template);
            if (!selectedTemplate) {
              setSelectedTemplate(defaultTemplate.id || '');
            }
          }
        }
      } else {
        console.error('Error fetching templates:', data.error || data);
        toast.error(data.error || 'Failed to load templates');
        setTemplates([]);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error(
        `Failed to load templates: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls') && !fileName.endsWith('.csv')) {
      toast.error('Please upload an Excel (.xlsx, .xls) or CSV (.csv) file');
      return;
    }

    setUploadedFile(file);
    setLoading(true);
    setParsedData(null);
    setFileData([]);
    setSelectedEmailColumn('');
    setSelectedNameColumn('');
    setCustomFields({});

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiFetch('/bulk-email/parse', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        setParsedData(data);
        toast.success(
          `File parsed successfully! Found ${data.totalRows} rows with ${data.columns.length} columns.`
        );

        // Auto-detect email column
        const emailColumn = data.columns.find(
          (col: string) =>
            col.toLowerCase().includes('email') || col.toLowerCase().includes('e-mail')
        );
        if (emailColumn) {
          setSelectedEmailColumn(emailColumn);
        }

        // Auto-detect name column
        const nameColumn = data.columns.find(
          (col: string) =>
            col.toLowerCase().includes('name') || col.toLowerCase().includes('full name')
        );
        if (nameColumn) {
          setSelectedNameColumn(nameColumn);
        }
      } else {
        throw new Error(data.error || 'Failed to parse file');
      }
    } catch (error) {
      console.error('Error parsing file:', error);
      toast.error(
        `Failed to parse file: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLoadFullData = async (): Promise<boolean> => {
    if (!uploadedFile || !parsedData) return false;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);

      const response = await apiFetch('/bulk-email/parse?full=true', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.success && Array.isArray(data.rows)) {
        const fullData = data.rows as Record<string, unknown>[];
        setFileData(fullData);
        setSelectedRecipients(new Set(fullData.map((_, index) => index)));
        setRecipientListPage(0);
        toast.success(`Loaded ${fullData.length} recipients`);
        return true;
      }
      toast.error('Could not load all rows from this file');
      return false;
    } catch (error) {
      console.error('Error loading full data:', error);
      toast.error('Failed to load full data');
      return false;
    } finally {
      setLoading(false);
    }
  };

  /** Parse one manual line: "Name, email", "Name <email>", tab-separated, or email-only. */
  const parseManualRecipientLine = (line: string): ManualRecipient | null => {
    const trimmed = line.trim();
    if (!trimmed) return null;

    const angleMatch = trimmed.match(/^(.+?)\s*<([^>]+@[^>]+)>$/);
    if (angleMatch) {
      return { name: angleMatch[1].trim(), email: angleMatch[2].trim() };
    }

    const commaParts = trimmed
      .split(/[,;]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (commaParts.length >= 2) {
      const emailPart = commaParts[commaParts.length - 1];
      if (emailPart.includes('@')) {
        const namePart = commaParts.slice(0, -1).join(', ').trim();
        return {
          name: namePart || emailPart.split('@')[0],
          email: emailPart,
        };
      }
    }

    const tabParts = trimmed
      .split(/\t+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (tabParts.length >= 2 && tabParts[tabParts.length - 1].includes('@')) {
      const emailPart = tabParts[tabParts.length - 1];
      const namePart = tabParts.slice(0, -1).join(' ').trim();
      return { name: namePart || emailPart.split('@')[0], email: emailPart };
    }

    if (trimmed.includes('@')) {
      return { email: trimmed, name: trimmed.split('@')[0] };
    }

    return null;
  };

  // Handle manual email entry (simple mode)
  const handleManualEmailsChange = (value: string) => {
    setManualEmails(value);
    const recipients: ManualRecipient[] = value
      .split('\n')
      .map((line) => parseManualRecipientLine(line))
      .filter((r): r is ManualRecipient => r !== null && r.email.includes('@'));

    setManualRecipients(recipients);
  };

  // Handle manual column-based data entry
  const handleManualColumnDataChange = (value: string) => {
    setManualColumnData(value);

    if (!value.trim()) {
      setManualParsedColumns([]);
      setManualParsedData([]);
      setManualRecipients([]);
      return;
    }

    try {
      const lines = value.split('\n').filter((line) => line.trim());
      if (lines.length === 0) {
        setManualParsedColumns([]);
        setManualParsedData([]);
        setManualRecipients([]);
        return;
      }

      // First line is headers
      const headers = lines[0]
        .split(',')
        .map((h) => h.trim().replace(/^"|"$/g, ''))
        .filter((h) => h.length > 0);

      if (headers.length === 0) {
        setManualParsedColumns([]);
        setManualParsedData([]);
        setManualRecipients([]);
        return;
      }

      setManualParsedColumns(headers);

      // Parse data rows
      const dataRows: any[] = [];
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        // Simple CSV parsing (handles quoted values)
        const values: string[] = [];
        let current = '';
        let inQuotes = false;

        for (let j = 0; j < line.length; j++) {
          const char = line[j];
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            values.push(current.trim().replace(/^"|"$/g, ''));
            current = '';
          } else {
            current += char;
          }
        }
        values.push(current.trim().replace(/^"|"$/g, ''));

        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        dataRows.push(row);
      }

      setManualParsedData(dataRows);

      // Auto-detect email and name columns
      const emailColumn =
        headers.find(
          (h: string) => h.toLowerCase().includes('email') || h.toLowerCase().includes('e-mail')
        ) || headers[0];

      const nameColumn = headers.find(
        (h: string) => h.toLowerCase().includes('name') || h.toLowerCase().includes('full name')
      );

      // Convert to recipients format
      const recipients: ManualRecipient[] = dataRows
        .map((row: any) => ({
          email: row[emailColumn] || '',
          name: nameColumn ? row[nameColumn] || '' : row[emailColumn]?.split('@')[0] || '',
        }))
        .filter((r: ManualRecipient) => r.email && r.email.includes('@'));

      setManualRecipients(recipients);
      setSelectedEmailColumn(emailColumn);
      if (nameColumn) {
        setSelectedNameColumn(nameColumn);
      }
    } catch (error) {
      console.error('Error parsing manual column data:', error);
      toast.error('Error parsing data. Please check the format.');
    }
  };

  // Toggle recipient selection
  const toggleRecipient = (index: number) => {
    const newSelected = new Set(selectedRecipients);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedRecipients(newSelected);
  };

  // Select all recipients
  const selectAllRecipients = () => {
    if (fileData.length > 0) {
      setSelectedRecipients(new Set(fileData.map((_, index) => index)));
    }
  };

  // Deselect all recipients
  const deselectAllRecipients = () => {
    setSelectedRecipients(new Set());
  };

  const getMinScheduleDatetime = () => {
    const d = new Date(Date.now() + 2 * 60_000);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const selectedSiteBrandingCheck = validateContentForSelectedSite(
    `${emailSubject}\n${emailTemplate}`,
    emailSites.selectedSiteId,
    emailSites.allBrands.length > 0 ? emailSites.allBrands : emailSites.sites
  );
  const selectedSiteBrandingError =
    selectedSiteBrandingCheck.ok === false ? selectedSiteBrandingCheck.error : null;
  const brandGuardSites =
    emailSites.allBrands.length > 0 ? emailSites.allBrands : emailSites.sites;

  const ensureSelectedSiteBrandingOk = (): boolean => {
    if (!selectedSiteBrandingError) return true;
    toast.error(selectedSiteBrandingError);
    return false;
  };

  const applySelectedWebsiteBranding = () => {
    if (!emailSites.selectedSiteId) {
      toast.error('Choose a website first.');
      return;
    }
    const nextSubject = rewriteContentToSelectedSite(
      emailSubject,
      emailSites.selectedSiteId,
      brandGuardSites
    );
    const nextTemplate = rewriteContentToSelectedSite(
      emailTemplate,
      emailSites.selectedSiteId,
      brandGuardSites
    );
    setEmailSubject(nextSubject);
    setEmailTemplate(nextTemplate);
    const check = validateContentForSelectedSite(
      `${nextSubject}\n${nextTemplate}`,
      emailSites.selectedSiteId,
      brandGuardSites
    );
    if (check.ok) {
      toast.success(`Updated to ${emailSites.selectedSite?.label ?? 'selected website'} branding`);
    } else {
      toast.error(check.ok === false ? check.error : 'Could not update branding');
    }
  };

  const handleScheduleEmails = async () => {
    if (!emailSubject || !emailTemplate) {
      toast.error('Please fill in email subject and template');
      return;
    }
    if (!ensureSelectedSiteBrandingOk()) return;
    if (!scheduledAt) {
      toast.error('Please choose date and time for delivery');
      return;
    }

    const activeManual = sendOverride?.manual ?? manualRecipients;
    const activeFileSelection = sendOverride?.fileIndices ?? selectedRecipients;

    const hasFileRecipients =
      (inputMode === 'file' || inputMode === 'both') &&
      uploadedFile &&
      selectedEmailColumn &&
      ((fileData.length > 0 && activeFileSelection.size > 0) ||
        (parsedData && parsedData.totalRows > 0));
    const hasManualRecipients =
      (inputMode === 'manual' || inputMode === 'both') && activeManual.length > 0;

    if (!hasFileRecipients && !hasManualRecipients) {
      toast.error('Please add recipients before scheduling');
      return;
    }

    setSending(true);
    try {
      const scheduledAtIso = new Date(scheduledAt).toISOString();
      const schedulePayload = {
        scheduledAt: scheduledAtIso,
        scheduledAtLocal: scheduledAt,
        timezone: scheduleTimezone,
      };

      if (hasFileRecipients && uploadedFile && selectedEmailColumn) {
        const selectedIndices =
          fileData.length > 0 && activeFileSelection.size > 0
            ? Array.from(activeFileSelection)
            : [];
        const formData = new FormData();
        formData.append('file', uploadedFile);
        formData.append('emailColumn', selectedEmailColumn);
        if (selectedNameColumn) formData.append('nameColumn', selectedNameColumn);
        formData.append('subject', emailSubject);
        formData.append('emailTemplate', emailTemplate);
        formData.append('scheduledAt', schedulePayload.scheduledAt);
        appendSendMetadata(formData);
        formData.append('scheduledAtLocal', schedulePayload.scheduledAtLocal);
        formData.append('timezone', schedulePayload.timezone);
        if (campaignName.trim()) formData.append('campaignName', campaignName.trim());
        if (fileData.length > 0 && selectedIndices.length > 0) {
          formData.append('selectedIndices', JSON.stringify(selectedIndices));
        }
        if (Object.keys(customFields).length > 0) {
          formData.append('customFields', JSON.stringify(customFields));
        }

        const response = await apiFetch('/bulk-email/schedule', { method: 'POST', body: formData });
        const data = await parseJsonResponse(response);
        if (response.status === 409) {
          throw new Error(String(data.error || 'This campaign is already scheduled at this time'));
        }
        if (!data.success)
          throw new Error(String(data.error || data.details || 'Failed to schedule'));
        toast.success(data.message ? String(data.message) : 'Campaign scheduled successfully');
      }

      if (hasManualRecipients) {
        const combinedRecipients = activeManual.map(
          (r): Record<string, string> => ({ email: r.email, name: r.name })
        );

        if (combinedRecipients.length > 0) {
          const response = await apiFetch('/bulk-email/schedule', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              fileData: combinedRecipients,
              emailColumn: 'email',
              nameColumn: 'name',
              subject: emailSubject,
              emailTemplate,
              ...sendMetadataJson(),
              ...schedulePayload,
              campaignName: campaignName.trim() || undefined,
              customFields: Object.keys(customFields).length > 0 ? customFields : undefined,
            }),
          });
          const data = await parseJsonResponse(response);
          if (response.status === 409) {
            throw new Error(
              String(data.error || 'This campaign is already scheduled at this time')
            );
          }
          if (!data.success)
            throw new Error(String(data.error || data.details || 'Failed to schedule'));
          toast.success(data.message ? String(data.message) : 'Campaign scheduled successfully');
        }
      }

      await scheduledCampaignsState.fetchCampaigns();
      setActiveTab('scheduled');
    } catch (error) {
      toast.error(`Schedule failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSending(false);
    }
  };

  const collectAudienceCandidates = (): AudienceCandidate[] => {
    const list: AudienceCandidate[] = [];
    const seen = new Set<string>();

    if (inputMode === 'manual' || inputMode === 'both') {
      for (const row of manualRecipients) {
        const key = row.email.trim().toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        list.push({ email: row.email.trim(), name: row.name });
      }
    }

    if ((inputMode === 'file' || inputMode === 'both') && selectedEmailColumn && fileData.length > 0) {
      const indices =
        selectedRecipients.size > 0
          ? Array.from(selectedRecipients)
          : fileData.map((_, index) => index);
      for (const idx of indices) {
        const row = fileData[idx];
        if (!row) continue;
        const email = String(row[selectedEmailColumn] ?? '').trim();
        if (!email) continue;
        const key = email.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        const name = selectedNameColumn
          ? String(row[selectedNameColumn] ?? '').trim() || 'Valued Customer'
          : 'Valued Customer';
        list.push({ email, name, fileIndex: idx });
      }
    }

    return list;
  };

  const openAudienceReview = async (action: 'send' | 'schedule') => {
    if (!emailSubject || !emailTemplate) {
      toast.error('Please fill in email subject and template');
      return;
    }
    if (!templateHasUtmTracking(emailTemplate)) {
      toast.error(utmTrackingRequirementMessage());
      setActiveTab('template');
      return;
    }
    if (!ensureSelectedSiteBrandingOk()) return;

    if (
      (inputMode === 'file' || inputMode === 'both') &&
      uploadedFile &&
      parsedData &&
      (fileData.length === 0 || !String(fileData[0]?.[selectedEmailColumn] ?? '').trim())
    ) {
      const loaded = await handleLoadFullData();
      if (!loaded) return;
    }

    const candidates = collectAudienceCandidates();
    if (candidates.length === 0) {
      toast.error('Add recipients first (upload a file or enter emails manually).');
      return;
    }

    setAudienceAction(action);
    setAudienceCandidates(candidates);
    setShowAudienceReview(true);
  };

  const handleAudienceReviewConfirm = async (selected: AudienceCandidate[]) => {
    const manual = selected.filter((row) => row.fileIndex === undefined);
    const fileIndices = new Set(
      selected.filter((row) => row.fileIndex !== undefined).map((row) => row.fileIndex as number)
    );
    setSendOverride({
      manual,
      fileIndices,
    });
    setShowAudienceReview(false);
    if (audienceAction === 'schedule') {
      await handleScheduleEmails();
    } else {
      await handleSendEmails();
    }
    setSendOverride(null);
  };

  const handleSendEmails = async () => {
    if (!emailSubject || !emailTemplate) {
      toast.error('Please fill in email subject and template');
      return;
    }
    if (!ensureSelectedSiteBrandingOk()) return;

    const activeManual = sendOverride?.manual ?? manualRecipients;
    const activeFileSelection = sendOverride?.fileIndices ?? selectedRecipients;

    // Check if we have recipients (from file or manual)
    const hasFileRecipients =
      (inputMode === 'file' || inputMode === 'both') &&
      uploadedFile &&
      selectedEmailColumn &&
      ((fileData.length > 0 && activeFileSelection.size > 0) || // Selected recipients from loaded data
        (parsedData &&
          parsedData.totalRows > 0 &&
          activeFileSelection.size === 0 &&
          fileData.length === 0)); // All recipients from file (not yet loaded)
    const hasManualRecipients =
      (inputMode === 'manual' || inputMode === 'both') && activeManual.length > 0;

    if (!hasFileRecipients && !hasManualRecipients) {
      toast.error('Please add recipients either by uploading a file or entering emails manually');
      return;
    }

    setSending(true);
    setSendResults([]);

    try {
      // Prepare recipients data - combine from all sources
      let allRecipientsToSend: Array<{ email: string; name: string }> = [];

      // Add selected file recipients
      if (hasFileRecipients && uploadedFile && selectedEmailColumn) {
        // For file recipients, we'll send the file and let backend parse
        // If fileData is empty but parsedData exists, send all (empty selectedIndices means all)
        // If fileData is loaded and recipients are selected, send only selected
        const selectedIndices =
          fileData.length > 0 && activeFileSelection.size > 0
            ? Array.from(activeFileSelection)
            : [];

        // Send file along with configuration
        const formData = new FormData();
        formData.append('file', uploadedFile);
        formData.append('emailColumn', selectedEmailColumn);
        if (selectedNameColumn) {
          formData.append('nameColumn', selectedNameColumn);
        }
        formData.append('subject', emailSubject);
        formData.append('emailTemplate', emailTemplate);
        appendSendMetadata(formData);
        // Only send selectedIndices if we have loaded data and selections
        if (fileData.length > 0 && selectedIndices.length > 0) {
          formData.append('selectedIndices', JSON.stringify(selectedIndices));
        }

        if (Object.keys(customFields).length > 0) {
          formData.append('customFields', JSON.stringify(customFields));
        }

        const response = await apiFetch('/bulk-email/send', {
          method: 'POST',
          body: formData,
        });

        const data = await response.json();

        if (data.success) {
          setSendResults(data.results || []);
          void emailCampaignHistory.fetchCampaigns();
          toast.success(
            `Bulk email campaign completed! ${data.summary.successful} successful, ${data.summary.failed} failed.`
          );
          setActiveTab('results');
        } else {
          throw new Error(data.error || 'Failed to send emails');
        }
      }

      if (hasManualRecipients) {
        const combinedRecipients = [...activeManual];

        if (combinedRecipients.length > 0) {
          const response = await apiFetch('/bulk-email/send', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              fileData: combinedRecipients,
              emailColumn: 'email',
              nameColumn: 'name',
              subject: emailSubject,
              emailTemplate,
              ...sendMetadataJson(),
              customFields: Object.keys(customFields).length > 0 ? customFields : undefined,
            }),
          });

          const data = await response.json();

          if (data.success) {
            // Merge results if we already have some from file
            setSendResults((prev) => [...prev, ...(data.results || [])]);
            void emailCampaignHistory.fetchCampaigns();
            const totalSuccessful =
              (sendResults.filter((r) => r.success).length || 0) + data.summary.successful;
            const totalFailed =
              (sendResults.filter((r) => !r.success).length || 0) + data.summary.failed;
            toast.success(
              `Bulk email campaign completed! ${totalSuccessful} successful, ${totalFailed} failed.`
            );
            setActiveTab('results');
          } else {
            throw new Error(data.error || 'Failed to send emails');
          }
        }
      }
    } catch (error) {
      console.error('Error sending emails:', error);
      toast.error(
        `Failed to send emails: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setSending(false);
    }
  };

  const handleSaveTemplate = async () => {
    // Better validation with specific error messages
    if (!templateName?.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    if (!emailSubject?.trim()) {
      toast.error('Please enter an email subject');
      return;
    }
    if (!emailTemplate?.trim()) {
      toast.error('Please enter email template content');
      return;
    }
    if (!ensureSelectedSiteBrandingOk()) return;
    if (!emailSites.selectedSiteId) {
      toast.error('Choose a website before saving this template.');
      return;
    }
    if (!templateHasUtmTracking(emailTemplate)) {
      toast.error(utmTrackingRequirementMessage());
      return;
    }

    try {
      const response = await apiFetch('/bulk-email/templates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: templateName,
          subject: emailSubject,
          template: emailTemplate,
          isDefault: false,
          siteId: emailSites.selectedSiteId,
          deliveryConfig: getCurrentDeliveryConfig(),
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Template saved successfully');
        setTemplateName('');
        fetchTemplates();
      } else {
        throw new Error(data.error || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(
        `Failed to save template: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  };

  const handleLoadTemplate = (template: EmailTemplate) => {
    setEmailSubject(template.subject);
    setEmailTemplate(template.template);
    if (templateMatchesSelectedSite(template)) {
      setSelectedTemplate(template.id || '');
      applyDeliveryConfig(template.delivery_config);
    } else {
      setSelectedTemplate('');
      toast.message(
        `Loaded "${template.name}" content for ${emailSites.selectedSite?.label ?? 'this website'}. Website settings were not changed because this template belongs to another website.`
      );
      return;
    }
    toast.success(`Template "${template.name}" loaded`);
  };

  const handleDeleteTemplate = async (id: string) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      const response = await apiFetch(`/bulk-email/templates/${id}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Template deleted successfully');
        fetchTemplates();
        if (selectedTemplate === id) {
          setSelectedTemplate('');
          setEmailSubject('');
          setEmailTemplate('');
        }
      } else {
        throw new Error(data.error || 'Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error(
        `Failed to delete template: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  };

  const getPreviewSampleRow = (): { row: Record<string, unknown>; fromRecipientData: boolean } => {
    if (parsedData?.preview?.[0]) {
      return { row: parsedData.preview[0], fromRecipientData: true };
    }
    if (manualParsedData.length > 0) {
      return { row: manualParsedData[0], fromRecipientData: true };
    }
    if (fileData.length > 0) {
      return { row: fileData[0], fromRecipientData: true };
    }
    if (manualRecipients.length > 0) {
      return {
        row: { name: manualRecipients[0].name, email: manualRecipients[0].email },
        fromRecipientData: true,
      };
    }
    return {
      row: { name: 'John Doe', email: 'john.doe@example.com' },
      fromRecipientData: false,
    };
  };

  const getTemplatePreviewDetails = () => {
    if (!emailTemplate.trim()) {
      return null;
    }

    const { row: sampleRow, fromRecipientData } = getPreviewSampleRow();
    const { siteLabel, siteUrl } = resolveSiteBranding(emailSites.selectedSite);

    return {
      ...buildTemplatePreview({
        template: emailTemplate,
        contentFormat: emailContentFormat,
        sampleRow,
        emailColumn: selectedEmailColumn || 'email',
        nameColumn: selectedNameColumn || undefined,
        customFields,
        siteLabel,
        siteUrl,
      }),
      subtitle: fromRecipientData
        ? `Using ${siteLabel} and the first recipient row for placeholders.`
        : `Using ${siteLabel} with sample data — upload recipients for real values.`,
    };
  };

  const insertPlaceholder = (placeholder: string) => {
    const field = emailMessageRef.current;
    if (!field) {
      setEmailTemplate((current) => `${current}${placeholder}`);
      return;
    }
    const start = field.selectionStart ?? emailTemplate.length;
    const end = field.selectionEnd ?? emailTemplate.length;
    const next = `${emailTemplate.slice(0, start)}${placeholder}${emailTemplate.slice(end)}`;
    setEmailTemplate(next);
    requestAnimationFrame(() => {
      field.focus();
      const cursor = start + placeholder.length;
      field.setSelectionRange(cursor, cursor);
    });
  };

  const handleOpenTemplatePreview = () => {
    if (!emailTemplate.trim()) {
      toast.error('Add your message first to preview.');
      return;
    }
    setShowTemplatePreview(true);
  };

  const templatePreviewDetails = showTemplatePreview ? getTemplatePreviewDetails() : null;

  return (
    <div className="space-y-4 sm:space-y-6">
      <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Emails</h2>

      <div className="px-0 py-1">
        {emailSites.loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading websites…
          </div>
        ) : emailSites.error ? (
          <p className="text-sm text-destructive">{emailSites.error}</p>
        ) : (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <label htmlFor="email-site" className="shrink-0 text-sm font-medium text-muted-foreground">
              Website
            </label>
            <Select
              value={emailSites.selectedSiteId}
              onValueChange={(siteId) => {
                emailSites.selectSite(siteId);
                if (emailSubject.trim() || emailTemplate.trim()) {
                  setEmailSubject((current) =>
                    rewriteContentToSelectedSite(current, siteId, brandGuardSites)
                  );
                  setEmailTemplate((current) =>
                    rewriteContentToSelectedSite(current, siteId, brandGuardSites)
                  );
                }
              }}
            >
              <SelectTrigger id="email-site" className="sm:max-w-md">
                <SelectValue placeholder="Choose website" />
              </SelectTrigger>
              <SelectContent>
                {emailSites.sites.map((site) => (
                  <SelectItem key={site.id} value={site.id}>
                    {site.label} ({site.domain})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="w-full">
          <TabsTrigger value="upload">Upload & Configure</TabsTrigger>
          <TabsTrigger value="template">Email Template</TabsTrigger>
          <TabsTrigger value="templates">Saved Templates</TabsTrigger>
          <TabsTrigger value="scheduled">
            Scheduled
            {activeScheduledCount > 0 && (
              <Badge variant="secondary" className="ml-2">
                {activeScheduledCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="history">
            History
            {emailCampaignHistory.total > 0 && (
              <Badge variant="secondary" className="ml-2">
                {emailCampaignHistory.total}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="results" disabled={sendResults.length === 0}>
            Results {sendResults.length > 0 && `(${sendResults.length})`}
          </TabsTrigger>
        </TabsList>

        {/* Upload & Configure Tab */}
        <TabsContent value="upload" className="space-y-4">
          {/* Input Mode Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Step 1: Choose Input Method
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Button
                  variant={inputMode === 'file' ? 'default' : 'outline'}
                  onClick={() => setInputMode('file')}
                  className="w-full justify-center sm:w-auto"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Upload File
                </Button>
                <Button
                  variant={inputMode === 'manual' ? 'default' : 'outline'}
                  onClick={() => setInputMode('manual')}
                  className="w-full justify-center sm:w-auto"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Manual Entry
                </Button>
                <Button
                  variant={inputMode === 'both' ? 'default' : 'outline'}
                  onClick={() => setInputMode('both')}
                  className="w-full justify-center sm:w-auto"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  File + Manual
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* File Upload Section */}
          {(inputMode === 'file' || inputMode === 'both') && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  Upload File (Excel/CSV)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                  <Input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileUpload}
                    disabled={loading}
                    className="w-full sm:max-w-md"
                  />
                  {uploadedFile && (
                    <Badge variant="outline" className="w-fit max-w-full gap-2 truncate">
                      <FileSpreadsheet className="h-4 w-4" />
                      {uploadedFile.name}
                    </Badge>
                  )}
                </div>

                {loading && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Parsing file...
                  </div>
                )}

                {parsedData && (
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Found <strong>{parsedData.totalRows}</strong> rows with{' '}
                        <strong>{parsedData.columns.length}</strong> columns
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        The full file will be processed when you send emails.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Available Columns:</h4>
                      <div className="flex flex-wrap gap-2">
                        {parsedData.columns.map((col) => (
                          <Badge key={col} variant="secondary">
                            {col}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Preview (first 5 rows):</h4>
                      <div className="table-scroll rounded-lg border">
                        <table className="min-w-full text-sm border">
                          <thead>
                            <tr className="bg-muted">
                              {parsedData.columns.map((col) => (
                                <th key={col} className="border p-2 text-left">
                                  {col}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {parsedData.preview.map((row, idx) => (
                              <tr key={idx}>
                                {parsedData.columns.map((col) => (
                                  <td key={col} className="border p-2">
                                    {row[col] || '-'}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Manual Email Entry Section */}
          {(inputMode === 'manual' || inputMode === 'both') && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Manual Email Entry
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Input Mode Toggle */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Input Format</label>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button
                      variant={manualInputMode === 'simple' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setManualInputMode('simple');
                        setManualColumnData('');
                        setManualParsedColumns([]);
                        setManualParsedData([]);
                      }}
                      className="w-full justify-center sm:w-auto"
                    >
                      Simple (Email Only)
                    </Button>
                    <Button
                      variant={manualInputMode === 'columns' ? 'default' : 'outline'}
                      size="sm"
                      className="w-full justify-center sm:w-auto"
                      onClick={() => {
                        setManualInputMode('columns');
                        setManualEmails('');
                        setManualRecipients([]);
                      }}
                    >
                      Column Data (CSV Format)
                    </Button>
                  </div>
                </div>

                {manualInputMode === 'simple' ? (
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Enter Email Addresses <span className="text-red-500">*</span>
                    </label>
                    <Textarea
                      value={manualEmails}
                      onChange={(e) => handleManualEmailsChange(e.target.value)}
                      placeholder="One recipient per line&#10;Rajesh Kumar, rajesh@agency.com&#10;Priya Sharma &lt;priya@tours.in&gt;&#10;support@company.com"
                      rows={8}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      One line per recipient. Use <strong>Name, email</strong> or{' '}
                      <strong>Name &lt;email&gt;</strong> so
                      {' {{'}RECIPIENT_GREETING{'}}'} / {'{{'}name{'}}'} personalize correctly.
                      Email-only lines use the part before @ as the name.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        Enter Column Data (CSV Format) <span className="text-red-500">*</span>
                      </label>
                      <Textarea
                        value={manualColumnData}
                        onChange={(e) => handleManualColumnDataChange(e.target.value)}
                        placeholder="Enter data with column headers (CSV format)&#10;Example:&#10;Name,Email,Phone&#10;John Doe,john@example.com,+1234567890&#10;Jane Smith,jane@example.com,+0987654321"
                        rows={10}
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        First line should contain column names (e.g., Name, Email, Phone). Each
                        subsequent line is a data row. Use commas to separate columns.
                      </p>
                    </div>

                    {manualParsedColumns.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2">Detected Columns:</h4>
                        <div className="flex flex-wrap gap-2">
                          {manualParsedColumns.map((col) => (
                            <Badge key={col} variant="secondary">
                              {col}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {manualParsedData.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2">
                          Preview Data ({manualParsedData.length} rows):
                        </h4>
                        <div className="table-scroll max-h-60 rounded-lg border">
                          <table className="min-w-full text-sm">
                            <thead className="bg-muted sticky top-0">
                              <tr>
                                {manualParsedColumns.map((col) => (
                                  <th key={col} className="p-2 text-left border">
                                    {col}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {manualParsedData.slice(0, 10).map((row, idx) => (
                                <tr key={idx} className="border-b">
                                  {manualParsedColumns.map((col) => (
                                    <td key={col} className="p-2 border">
                                      {row[col] || '-'}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {manualParsedData.length > 10 && (
                            <div className="p-2 text-xs text-muted-foreground text-center border-t">
                              Showing first 10 rows of {manualParsedData.length} total rows
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {manualRecipients.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold">Recipients ({manualRecipients.length})</h4>
                      <Badge variant="outline">{manualRecipients.length} emails</Badge>
                    </div>
                    <div className="max-h-60 overflow-y-auto border rounded-lg p-2 space-y-1">
                      {manualRecipients.map((recipient, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 p-2 hover:bg-muted rounded"
                        >
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium">{recipient.name}</span>
                          <span className="text-sm">{recipient.email}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {parsedData && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Step 2: Select Columns & Recipients
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Email Column <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={selectedEmailColumn || undefined}
                    onValueChange={setSelectedEmailColumn}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select email column" />
                    </SelectTrigger>
                    <SelectContent>
                      {parsedData.columns.map((col) => (
                        <SelectItem key={col} value={col}>
                          {col}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Name Column (optional)</label>
                  <Select
                    value={selectedNameColumn || '__none__'}
                    onValueChange={(value) =>
                      setSelectedNameColumn(value === '__none__' ? '' : value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select name column (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None (optional)</SelectItem>
                      {parsedData.columns.map((col) => (
                        <SelectItem key={col} value={col}>
                          {col}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Use {'{{'}name{'}}'} or {'{{'}RECIPIENT_GREETING{'}}'} (e.g. &quot;Dear
                    Rajesh,&quot;) in your template
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Custom Fields (optional)</label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Map template variables to columns. Use {'{{'}variableName{'}}'} in your
                    template.
                  </p>
                  <div className="space-y-2">
                    {Object.entries(customFields).map(([varName, colName], idx) => (
                      <div key={idx} className="flex flex-col gap-2 sm:flex-row">
                        <Input
                          placeholder="Variable name (e.g., company)"
                          value={varName}
                          onChange={(e) => {
                            const newFields = { ...customFields };
                            delete newFields[varName];
                            newFields[e.target.value] = colName;
                            setCustomFields(newFields);
                          }}
                          className="flex-1"
                        />
                        <Select
                          value={colName || '__none__'}
                          onValueChange={(value) => {
                            setCustomFields({
                              ...customFields,
                              [varName]: value === '__none__' ? '' : value,
                            });
                          }}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Select column" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">Select column</SelectItem>
                            {parsedData.columns.map((col) => (
                              <SelectItem key={col} value={col}>
                                {col}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newFields = { ...customFields };
                            delete newFields[varName];
                            setCustomFields(newFields);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCustomFields({ ...customFields, '': '' })}
                    >
                      Add Custom Field
                    </Button>
                  </div>
                </div>

                {/* Recipient Selection */}
                {selectedEmailColumn && fileData.length > 0 && (
                  <div className="mt-6 pt-6 border-t">
                    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <h4 className="font-semibold">Select Recipients</h4>
                        <p className="text-sm text-muted-foreground">
                          Choose which recipients to send emails to ({selectedRecipients.size} of{' '}
                          {fileData.length} selected)
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={selectAllRecipients}>
                          Select All
                        </Button>
                        <Button variant="outline" size="sm" onClick={deselectAllRecipients}>
                          Deselect All
                        </Button>
                      </div>
                    </div>
                    <div className="table-scroll max-h-96 rounded-lg border">
                      <table className="min-w-full text-sm">
                        <thead className="bg-muted sticky top-0">
                          <tr>
                            <th className="p-2 text-left w-12">
                              <input
                                type="checkbox"
                                checked={
                                  selectedRecipients.size === fileData.length && fileData.length > 0
                                }
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    selectAllRecipients();
                                  } else {
                                    deselectAllRecipients();
                                  }
                                }}
                                className="cursor-pointer"
                              />
                            </th>
                            <th className="p-2 text-left">Email</th>
                            {selectedNameColumn && <th className="p-2 text-left">Name</th>}
                            {parsedData.columns.slice(0, 3).map(
                              (col) =>
                                col !== selectedEmailColumn &&
                                col !== selectedNameColumn && (
                                  <th key={col} className="p-2 text-left">
                                    {col}
                                  </th>
                                )
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {fileData
                            .slice(
                              recipientListPage * RECIPIENT_TABLE_PAGE_SIZE,
                              (recipientListPage + 1) * RECIPIENT_TABLE_PAGE_SIZE
                            )
                            .map((row, sliceIdx) => {
                            const idx =
                              recipientListPage * RECIPIENT_TABLE_PAGE_SIZE + sliceIdx;
                            const email = row[selectedEmailColumn];
                            const name = selectedNameColumn ? row[selectedNameColumn] : '';
                            const isSelected = selectedRecipients.has(idx);

                            return (
                              <tr
                                key={idx}
                                className={`border-b hover:bg-muted cursor-pointer ${isSelected ? 'bg-primary/10' : ''}`}
                                onClick={() => toggleRecipient(idx)}
                              >
                                <td className="p-2">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleRecipient(idx)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="cursor-pointer"
                                  />
                                </td>
                                <td className="p-2">{email || '-'}</td>
                                {selectedNameColumn && <td className="p-2">{name || '-'}</td>}
                                {parsedData.columns.slice(0, 3).map(
                                  (col) =>
                                    col !== selectedEmailColumn &&
                                    col !== selectedNameColumn && (
                                      <td key={col} className="p-2">
                                        {row[col] || '-'}
                                      </td>
                                    )
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {fileData.length > RECIPIENT_TABLE_PAGE_SIZE && (
                        <div className="flex items-center justify-between gap-2 p-2 text-xs text-muted-foreground border-t">
                          <span>
                            Page {recipientListPage + 1} of{' '}
                            {Math.ceil(fileData.length / RECIPIENT_TABLE_PAGE_SIZE)} ({fileData.length}{' '}
                            recipients)
                          </span>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={recipientListPage === 0}
                              onClick={() => setRecipientListPage((p) => Math.max(0, p - 1))}
                            >
                              Previous
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={
                                (recipientListPage + 1) * RECIPIENT_TABLE_PAGE_SIZE >=
                                fileData.length
                              }
                              onClick={() => setRecipientListPage((p) => p + 1)}
                            >
                              Next
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Email Template Tab */}
        <TabsContent value="template" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Step 3: Create Email Template
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Template Name <span className="text-red-500">*</span>
                </label>
                <Input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g., Voting Email Template, Welcome Email, etc."
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Give your template a descriptive name for easy identification
                </p>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Email Subject <span className="text-red-500">*</span>
                </label>
                <Input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Enter email subject"
                />
                {selectedSiteBrandingError ? (
                  <p className="mt-2 text-sm text-destructive" role="alert">
                    Subject or message has another website name or link. Fix it before sending.
                  </p>
                ) : null}
              </div>

              <div>
                <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <label className="text-sm font-medium">
                    Email message <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button
                      type="button"
                      size="sm"
                      variant={emailContentFormat === 'html' ? 'default' : 'outline'}
                      className="w-full justify-center sm:w-auto"
                      onClick={() => setEmailContentFormat('html')}
                    >
                      HTML
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={emailContentFormat === 'text' ? 'default' : 'outline'}
                      className="w-full justify-center sm:w-auto"
                      onClick={() => setEmailContentFormat('text')}
                    >
                      Plain message
                    </Button>
                  </div>
                </div>
                {emailSites.selectedSite ? (
                  <div className="mb-3 rounded-xl bg-muted/50 px-4 py-3 text-sm">
                    <p className="font-medium text-foreground">
                      Using {emailSites.selectedSite.label}
                    </p>
                    <p className="mt-1 text-muted-foreground">
                      Website name fills{' '}
                      <code className="text-foreground">{'{{SITE_NAME}}'}</code> as{' '}
                      <span className="font-medium text-foreground">
                        {resolveSiteBranding(emailSites.selectedSite).siteLabel}
                      </span>
                      . Website link fills{' '}
                      <code className="text-foreground">{'{{SITE_URL}}'}</code> and{' '}
                      <code className="text-foreground">{'{{CTA_URL}}'}</code> from{' '}
                      <span className="font-medium text-foreground">
                        {resolveSiteBranding(emailSites.selectedSite).siteUrl}
                      </span>
                      .
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => insertPlaceholder('{{SITE_NAME}}')}
                      >
                        Add website name
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => insertPlaceholder('{{SITE_URL}}')}
                      >
                        Add website link
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => insertPlaceholder('{{CTA_URL}}')}
                      >
                        Add login link
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="mb-3 text-sm text-muted-foreground">
                    Choose a website above so this message can use that brand name and link.
                  </p>
                )}
                <Textarea
                  ref={emailMessageRef}
                  value={emailTemplate}
                  onChange={(e) => setEmailTemplate(e.target.value)}
                  placeholder={
                    emailContentFormat === 'text'
                      ? 'Write your message here. Use {{SITE_NAME}} and {{SITE_URL}} for the selected website.'
                      : 'Paste HTML here. Use {{SITE_NAME}} and {{SITE_URL}} for the selected website.'
                  }
                  rows={20}
                  className={emailContentFormat === 'html' ? 'font-mono text-sm' : 'text-sm'}
                />
                {selectedSiteBrandingError ? (
                  <div className="mt-2 space-y-2" role="alert">
                    <p className="text-sm text-destructive">{selectedSiteBrandingError}</p>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={applySelectedWebsiteBranding}
                    >
                      Replace with {emailSites.selectedSite?.label ?? 'selected website'} branding
                    </Button>
                  </div>
                ) : null}
                <p className="mt-2 text-xs text-muted-foreground">
                  Only {emailSites.selectedSite?.label ?? 'the selected website'} name and links are
                  allowed here. Optional: <code>{'{{name}}'}</code>,{' '}
                  <code>{'{{columnName}}'}</code>, <code>{'{{SITE_NAME}}'}</code>,{' '}
                  <code>{'{{SITE_URL}}'}</code>, <code>{'{{CTA_URL}}'}</code>
                </p>
              </div>

              <div className="flex gap-2 flex-wrap">
                <Button
                  type="button"
                  onClick={handleOpenTemplatePreview}
                  variant="outline"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Show Preview
                </Button>
                <Button
                  onClick={handleSaveTemplate}
                  variant="outline"
                  disabled={Boolean(selectedSiteBrandingError)}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Template
                </Button>
              </div>

              <Dialog open={showTemplatePreview} onOpenChange={setShowTemplatePreview}>
                <DialogContent className="flex max-h-[90dvh] w-[calc(100%-2rem)] max-w-4xl flex-col gap-4 overflow-hidden sm:w-full">
                  <DialogHeader>
                    <DialogTitle>Template Preview</DialogTitle>
                    {templatePreviewDetails && (
                      <DialogDescription>{templatePreviewDetails.subtitle}</DialogDescription>
                    )}
                  </DialogHeader>
                  {templatePreviewDetails &&
                    (templatePreviewDetails.contentFormat === 'text' ? (
                      <pre className="min-h-[12rem] flex-1 overflow-auto whitespace-pre-wrap rounded-md border bg-card p-4 text-sm text-foreground">
                        {templatePreviewDetails.body}
                      </pre>
                    ) : (
                      <div
                        className="prose min-h-[12rem] max-w-none flex-1 overflow-auto rounded-md border bg-white p-4"
                        dangerouslySetInnerHTML={{ __html: templatePreviewDetails.body }}
                      />
                    ))}
                </DialogContent>
              </Dialog>

              <div className="mt-4 rounded-lg bg-primary/10 p-4">
                <h4 className="mb-2 font-semibold">Tips</h4>
                <ul className="list-inside list-disc space-y-1 text-sm">
                  <li>
                    <strong>Plain message:</strong> Write normal text — no HTML needed
                  </li>
                  <li>
                    <strong>HTML:</strong> Use rich formatting when you need layouts or buttons
                  </li>
                  <li>
                    <strong>Website branding:</strong> Use <code>{'{{SITE_NAME}}'}</code> and{' '}
                    <code>{'{{SITE_URL}}'}</code> so the selected website name and link fill in
                    automatically
                  </li>
                  <li>
                    <strong>Placeholders:</strong> Use <code>{'{{name}}'}</code> or column names for
                    personalization
                  </li>
                  <li>
                    <strong>Tracking (required):</strong> Include <code>{'{{CTA_URL}}'}</code> or{' '}
                    <code>utm_source=</code> and <code>utm_medium=</code> on links
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {emailSubject && emailTemplate && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5" />
                  Step 4: Send Emails
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/10">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                      <div>
                        <p className="font-semibold text-foreground">Ready to Send</p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {(() => {
                            // Calculate file count: use selectedRecipients if available, otherwise fileData length, otherwise parsedData totalRows
                            let fileCount = 0;
                            if (inputMode === 'file' || inputMode === 'both') {
                              if (uploadedFile && selectedEmailColumn) {
                                if (fileData.length > 0 && selectedRecipients.size > 0) {
                                  fileCount = selectedRecipients.size;
                                } else if (fileData.length > 0) {
                                  fileCount = fileData.length;
                                } else if (parsedData && parsedData.totalRows > 0) {
                                  fileCount = parsedData.totalRows;
                                }
                              }
                            }

                            const manualCount =
                              inputMode === 'manual' || inputMode === 'both'
                                ? manualRecipients.length
                                : 0;
                            const total = fileCount + manualCount;

                            if (total === 0) {
                              return 'Please add recipients by uploading a file or entering emails manually.';
                            }

                            const parts = [];
                            if (fileCount > 0) parts.push(`${fileCount} from file`);
                            if (manualCount > 0) parts.push(`${manualCount} manual`);

                            return `You're about to send emails to ${total} recipient${total !== 1 ? 's' : ''} (${parts.join(', ')}). Make sure your template and subject are correct.`;
                          })()}
                        </p>
                      </div>
                    </div>
                  </div>

                  {selectedSiteBrandingError ? (
                    <div
                      className="rounded-lg border border-destructive/30 bg-destructive/10 p-4"
                      role="alert"
                    >
                      <div className="flex items-start gap-2">
                        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                        <div className="space-y-2">
                          <p className="font-semibold text-foreground">
                            Sending is turned off for now
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {selectedSiteBrandingError}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Remove the other brand name or link from the subject/message, or use the
                            replace button above, then try again.
                          </p>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={applySelectedWebsiteBranding}
                          >
                            Replace with {emailSites.selectedSite?.label ?? 'selected website'}{' '}
                            branding
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  <div className="space-y-3 p-4 bg-muted rounded-lg border border">
                    <p className="font-semibold text-foreground">When should emails go out?</p>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        type="button"
                        variant={sendMode === 'now' ? 'default' : 'outline'}
                        onClick={() => setSendMode('now')}
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Send now
                      </Button>
                      <Button
                        type="button"
                        variant={sendMode === 'schedule' ? 'default' : 'outline'}
                        onClick={() => setSendMode('schedule')}
                      >
                        <Clock className="h-4 w-4 mr-2" />
                        Schedule for later
                      </Button>
                    </div>
                    {sendMode === 'schedule' && (
                      <div className="grid gap-3 sm:grid-cols-2 pt-2">
                        <div>
                          <label className="text-sm font-medium text-foreground block mb-1">
                            Delivery date & time
                          </label>
                          <Input
                            type="datetime-local"
                            min={getMinScheduleDatetime()}
                            value={scheduledAt}
                            onChange={(e) => setScheduledAt(e.target.value)}
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            At least 1 minute from now. Sends within ~5 seconds of this time.
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-foreground block mb-1">
                            Timezone
                          </label>
                          <Select value={scheduleTimezone} onValueChange={setScheduleTimezone}>
                            <SelectTrigger>
                              <SelectValue placeholder="Timezone" />
                            </SelectTrigger>
                            <SelectContent>
                              {COMMON_TIMEZONES.map((tz) => (
                                <SelectItem key={tz} value={tz}>
                                  {tz}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="sm:col-span-2">
                          <label className="text-sm font-medium text-foreground block mb-1">
                            Campaign label (optional)
                          </label>
                          <Input
                            placeholder="e.g. Tour operators — Sri Lanka voting"
                            value={campaignName}
                            onChange={(e) => setCampaignName(e.target.value)}
                          />
                        </div>
                        {sendConfig && (
                          <div className="space-y-2 rounded-lg border bg-card p-3 sm:col-span-2">
                            <p className="text-sm font-medium text-foreground">
                              Send speed (scheduled campaigns)
                            </p>
                            <label className="text-xs text-muted-foreground block">
                              Delay between emails: {sendConfig.sendDelayMs}ms
                            </label>
                            <input
                              type="range"
                              min={300}
                              max={2000}
                              step={100}
                              value={sendConfig.sendDelayMs}
                              onChange={(e) =>
                                setSendConfig({
                                  ...sendConfig,
                                  sendDelayMs: Number(e.target.value),
                                })
                              }
                              onMouseUp={() =>
                                saveSendConfig({ sendDelayMs: sendConfig.sendDelayMs })
                              }
                              onTouchEnd={() =>
                                saveSendConfig({ sendDelayMs: sendConfig.sendDelayMs })
                              }
                              className="w-full"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {(emailSubject || emailTemplate) && (
                    <div className="rounded-lg border bg-card p-3 text-sm text-muted-foreground">
                      <p className="font-semibold text-foreground mb-1">Default content in use</p>
                      <p>
                        <strong>Subject:</strong> {emailSubject || '—'}
                      </p>
                      <p className="mt-1 line-clamp-2">
                        <strong>Template:</strong>{' '}
                        {emailTemplate ? `${emailTemplate.slice(0, 120)}…` : '—'}
                      </p>
                    </div>
                  )}

                  <Button
                    onClick={() =>
                      void openAudienceReview(sendMode === 'schedule' ? 'schedule' : 'send')
                    }
                    disabled={(() => {
                      if (sending) return true;
                      if (selectedSiteBrandingError) return true;
                      if (!emailSites.selectedSiteId || emailSites.loading) return true;
                      if (sendMode === 'schedule' && !scheduledAt) return true;
                      const hasFile =
                        uploadedFile &&
                        selectedEmailColumn &&
                        (parsedData?.totalRows > 0 || fileData.length > 0);
                      const hasManual = manualRecipients.length > 0;

                      if (inputMode === 'file') return !hasFile;
                      if (inputMode === 'manual') return !hasManual;
                      if (inputMode === 'both') return !hasFile && !hasManual;
                      return true;
                    })()}
                    className="w-full"
                    size="lg"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {sendMode === 'schedule' ? 'Scheduling…' : 'Sending Emails...'}
                      </>
                    ) : sendMode === 'schedule' ? (
                      <>
                        <CalendarClock className="h-4 w-4 mr-2" />
                        Schedule campaign (
                        {(() => {
                          // Calculate file count: use selectedRecipients if available, otherwise fileData length, otherwise parsedData totalRows
                          let fileCount = 0;
                          if (inputMode === 'file' || inputMode === 'both') {
                            if (uploadedFile && selectedEmailColumn) {
                              if (fileData.length > 0 && selectedRecipients.size > 0) {
                                fileCount = selectedRecipients.size;
                              } else if (fileData.length > 0) {
                                fileCount = fileData.length;
                              } else if (parsedData && parsedData.totalRows > 0) {
                                fileCount = parsedData.totalRows;
                              }
                            }
                          }
                          const manualCount =
                            inputMode === 'manual' || inputMode === 'both'
                              ? manualRecipients.length
                              : 0;
                          return fileCount + manualCount;
                        })()}{' '}
                        recipients)
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Bulk Emails (
                        {(() => {
                          let fileCount = 0;
                          if (inputMode === 'file' || inputMode === 'both') {
                            if (uploadedFile && selectedEmailColumn) {
                              if (fileData.length > 0 && selectedRecipients.size > 0) {
                                fileCount = selectedRecipients.size;
                              } else if (fileData.length > 0) {
                                fileCount = fileData.length;
                              } else if (parsedData && parsedData.totalRows > 0) {
                                fileCount = parsedData.totalRows;
                              }
                            }
                          }
                          const manualCount =
                            inputMode === 'manual' || inputMode === 'both'
                              ? manualRecipients.length
                              : 0;
                          return fileCount + manualCount;
                        })()}{' '}
                        recipients)
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="scheduled" className="space-y-4">
          <ScheduledCampaignsPanel {...scheduledCampaignsState} />
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <EmailCampaignHistoryPanel {...emailCampaignHistory} />
        </TabsContent>

        {/* Saved Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle>Saved Email Templates</CardTitle>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Showing templates for{' '}
                    {emailSites.selectedSite?.label ?? 'the selected website'} only.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchTemplates}
                  disabled={loadingTemplates}
                  className="w-full justify-center sm:w-auto"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loadingTemplates ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingTemplates ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading templates...
                </div>
              ) : templatesForSelectedSite.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground mb-4">
                    No templates saved for{' '}
                    {emailSites.selectedSite?.label ?? 'this website'} yet.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Save a template from the Email Template tab while this website is selected.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {templatesForSelectedSite.map((template) => (
                    <div
                      key={template.id}
                      className="flex flex-col gap-3 rounded-xl bg-muted/50 p-4 transition-colors hover:bg-muted sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0 flex-1">
                        <h4 className="font-semibold">{template.name}</h4>
                        <p className="text-sm text-muted-foreground">{template.subject}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {emailSites.selectedSite?.label ?? 'Website'} · Created:{' '}
                          {new Date(template.created_at || '').toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleLoadTemplate(template)}
                        >
                          Load
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => template.id && handleDeleteTemplate(template.id)}
                          className="text-destructive border-destructive hover:bg-destructive hover:text-white"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Results Tab */}
        <TabsContent value="results" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5" />
                  Send Results
                </CardTitle>
                {sendResults.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setActiveTab('history');
                      void emailCampaignHistory.fetchCampaigns();
                    }}
                  >
                    View in history
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {sendResults.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  <Send className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No results yet</p>
                  <p className="text-sm mt-1">
                    Send emails from the Email Template tab to see results here.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary cards */}
                  {(() => {
                    const successCount = sendResults.filter((r) => r.success).length;
                    const failCount = sendResults.filter((r) => !r.success).length;
                    const successRate =
                      sendResults.length > 0
                        ? Math.round((successCount / sendResults.length) * 100)
                        : 0;
                    return (
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                        <div className="rounded-lg border bg-card p-3 text-center">
                          <p className="text-2xl font-bold">{sendResults.length}</p>
                          <p className="text-xs text-muted-foreground">Total</p>
                        </div>
                        <div className="rounded-lg border bg-card p-3 text-center">
                          <p className="text-2xl font-bold text-green-600">{successCount}</p>
                          <p className="text-xs text-muted-foreground">Sent</p>
                        </div>
                        <div className="rounded-lg border bg-card p-3 text-center">
                          <p className={`text-2xl font-bold ${failCount > 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                            {failCount}
                          </p>
                          <p className="text-xs text-muted-foreground">Failed</p>
                        </div>
                        <div className="rounded-lg border bg-card p-3 text-center">
                          <p
                            className={`text-2xl font-bold ${
                              successRate === 100
                                ? 'text-green-600'
                                : successRate >= 90
                                  ? 'text-foreground'
                                  : 'text-amber-600'
                            }`}
                          >
                            {successRate}%
                          </p>
                          <p className="text-xs text-muted-foreground">Success rate</p>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Failed recipients alert */}
                  {sendResults.some((r) => !r.success) && (
                    <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/30">
                      <XCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-800 dark:text-red-400">
                          {sendResults.filter((r) => !r.success).length} email{sendResults.filter((r) => !r.success).length !== 1 ? 's' : ''} failed to send
                        </p>
                        <p className="text-xs text-red-600 dark:text-red-500 mt-1">
                          Check the error messages below. Common causes: invalid email address, rate limit, or provider issue.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="table-scroll max-h-96 rounded-lg border">
                    <table className="min-w-full text-sm">
                      <thead className="bg-muted sticky top-0">
                        <tr>
                          <th className="p-2 text-left w-8">#</th>
                          <th className="p-2 text-left">Email</th>
                          <th className="p-2 text-left">Name</th>
                          <th className="p-2 text-left">Status</th>
                          <th className="p-2 text-left">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sendResults.map((result, idx) => (
                          <tr
                            key={idx}
                            className={`border-b ${!result.success ? 'bg-red-50/50 dark:bg-red-950/10' : ''}`}
                          >
                            <td className="p-2 text-xs text-muted-foreground">{idx + 1}</td>
                            <td className="p-2 font-mono text-xs">{result.email}</td>
                            <td className="p-2">{result.name || '—'}</td>
                            <td className="p-2">
                              {result.success ? (
                                <Badge variant="outline" className="gap-1 text-green-600">
                                  <CheckCircle className="h-3 w-3" />
                                  Sent
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="gap-1 text-red-600">
                                  <XCircle className="h-3 w-3" />
                                  Failed
                                </Badge>
                              )}
                            </td>
                            <td className="p-2">
                              {result.success ? (
                                <span className="text-xs text-muted-foreground font-mono">
                                  {result.messageId?.slice(0, 16)}…
                                </span>
                              ) : (
                                <span className="text-xs text-red-600">{result.error}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <SendAudienceReviewDialog
        open={showAudienceReview}
        onOpenChange={setShowAudienceReview}
        candidates={audienceCandidates}
        siteId={emailSites.selectedSiteId || ''}
        onConfirm={(selected) => void handleAudienceReviewConfirm(selected)}
        confirming={sending}
      />
    </div>
  );
};

export default BulkEmailManagement;
